import React from 'react';
import {FadeIn} from '../../components/ui/FadeIn';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiClock,FiDollarSign,FiTrendingUp,FiZap,FiCheck}=FiIcons;

const CourseCreators=()=> {
  return (
    <div className="pt-20 pb-32 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center mb-16">
        <h1 className="text-4xl md:text-6xl font-black mb-6 text-white drop-shadow-[0_2px_40px_rgba(255,255,255,0.3)]">
          Turn your YouTube channel into a course library
        </h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
          Stop spending 20+ hours manually converting videos to courses. Get the same output in 30 minutes.
        </p>
      </FadeIn>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
        <FadeIn delay={0.1}>
          <div className="glass-card p-6 text-center border border-brand-electric/40">
            <SafeIcon icon={FiClock} className="text-4xl text-brand-electric mx-auto mb-3" />
            <div className="text-3xl font-black text-white mb-2">92%</div>
            <p className="text-sm text-white/70">Time Saved</p>
          </div>
        </FadeIn>
        <FadeIn delay={0.2}>
          <div className="glass-card p-6 text-center border border-brand-gold/40">
            <SafeIcon icon={FiDollarSign} className="text-4xl text-brand-gold mx-auto mb-3" />
            <div className="text-3xl font-black text-white mb-2">$8k+</div>
            <p className="text-sm text-white/70">Avg Monthly Revenue</p>
          </div>
        </FadeIn>
        <FadeIn delay={0.3}>
          <div className="glass-card p-6 text-center border border-brand-mint/40">
            <SafeIcon icon={FiTrendingUp} className="text-4xl text-brand-mint mx-auto mb-3" />
            <div className="text-3xl font-black text-white mb-2">10x</div>
            <p className="text-sm text-white/70">Content Output</p>
          </div>
        </FadeIn>
        <FadeIn delay={0.4}>
          <div className="glass-card p-6 text-center border border-brand-neon/40">
            <SafeIcon icon={FiZap} className="text-4xl text-brand-neon mx-auto mb-3" />
            <div className="text-3xl font-black text-white mb-2">15 min</div>
            <p className="text-sm text-white/70">Per Transformation</p>
          </div>
        </FadeIn>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <FadeIn delay={0.1}>
          <div className="glass-card p-8 border border-brand-flame/40 hover:border-brand-flame/60 transition-all">
            <h3 className="text-xl font-bold text-brand-flame mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-flame animate-pulse"></span>
              The Old Way (18+ hours)
            </h3>
            <ul className="space-y-4 text-white/70">
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Watch recording</span> <span className="text-white">2 hrs</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Write slide outline</span> <span className="text-white">3 hrs</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Design slides in Canva</span> <span className="text-white">5 hrs</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Record narration</span> <span className="text-white">2 hrs</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Edit video</span> <span className="text-white">4 hrs</span></li>
              <li className="flex justify-between"><span>Export in 3 formats</span> <span className="text-white">2 hrs</span></li>
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.2}>
          <div className="glass-card p-8 border border-brand-mint/60 bg-brand-mint/5 shadow-[0_0_30px_rgba(16,185,129,0.2)] hover:border-brand-mint/80 transition-all">
            <h3 className="text-xl font-bold text-brand-mint mb-6 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand-mint animate-pulse"></span>
              The VidRevive Way (Under 15 mins)
            </h3>
            <ul className="space-y-4 text-white">
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Paste YouTube URL</span> <span className="text-brand-electric">30 sec</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Review AI-generated structure</span> <span className="text-brand-electric">5 min</span></li>
              <li className="flex justify-between border-b border-white/10 pb-2"><span>Approve slides + voice</span> <span className="text-brand-electric">5 min</span></li>
              <li className="flex justify-between"><span>Download video + PDF + PPTX</span> <span className="text-brand-electric">2 min</span></li>
            </ul>
          </div>
        </FadeIn>
      </div>

      {/* What You Get Section */}
      <FadeIn className="mb-16">
        <div className="glass-card p-8 border border-brand-electric/40">
          <h3 className="text-2xl font-bold text-white mb-6 text-center">What You Get With Every Transformation</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Professional slide deck (PPTX)',
              'PDF handouts for students',
              'Narrated video with your voice',
              'Motion graphics (optional)',
              'Custom branding applied',
              'Learning objectives auto-generated',
              'Key takeaways extracted',
              'Ready for LMS upload'
            ].map((item,idx)=> (
              <div key={idx} className="flex items-center gap-3">
                <SafeIcon icon={FiCheck} className="text-brand-electric shrink-0" />
                <span className="text-white/90">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      {/* CTA */}
      <FadeIn className="text-center">
        <button className="btn-cta py-4 px-12 text-lg mb-3 text-brand-neon">
          Start Free
        </button>
        <p className="text-sm text-white/60">No credit card required</p>
      </FadeIn>
    </div>
  );
};

export default CourseCreators;